// Provider reassign — ProviderNumberReassignController (/v1/numbers/provider).
//
// A provider moves numbers it already holds between its own organization and its
// businesses. The tenure (billing) moves at once; the switch label follows through the
// same audited push as the master's "Push to switch", reported per move as `push`.
//
// Shapes: the list/target/history rows are maps the server builds in snake_case; `push`
// is a Java record (camelCase), and `number` is the snake_case ProviderNumber entity.
import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { responseFrom, errorFrom } from '../apiEnvelope';
import type { APIResponse } from 'types/ivr';
import type { ProviderNumber } from './providers.service';
import type { SwitchPushAction, SwitchPushMode, SwitchPushPlan, SwitchPushRecord } from './switchPush.service';

export interface ProviderOrg {
  id: string;
  name: string;
  business_id?: string | null;
  type?: 'SERVICE' | 'BUSINESS';
  is_provider: boolean;
}

export interface ProviderTarget extends ProviderOrg {
  assignable: boolean;
  reason?: string;
}

export interface ProviderHeldNumber {
  number_id: number;
  e164: string;
  provider_id?: number | null;
  provider_name?: string | null;
  status: string;
  business_id?: string | null;
  holder_organization_id: string;
  holder_name: string;
  holder_business_id?: string | null;
  held_by_provider: boolean;
  held_since?: unknown;
  lock_in_ends_at?: unknown;
}

export interface ProviderNumbersList {
  provider: ProviderOrg;
  switch_push_mode: SwitchPushMode;
  numbers: ProviderHeldNumber[];
}

export interface ProviderTargets {
  provider: ProviderOrg;
  targets: ProviderTarget[];
}

/** OK | NO_CHANGE | BLOCKED | FAILED in apply mode; PREVIEW or OFF otherwise. */
export type ProviderPushOutcome = 'OK' | 'NO_CHANGE' | 'BLOCKED' | 'FAILED' | 'PREVIEW' | 'OFF';

export interface ProviderPush {
  mode: SwitchPushMode;
  outcome: ProviderPushOutcome;
  action?: SwitchPushAction | null;
  reason?: string | null;
  newLabel?: string | null;
  pushId?: number | null;
  cacheRefreshed?: boolean | null;
  warnings?: string[];
  plan?: SwitchPushPlan | null;
}

export interface ProviderMoveResult {
  number: ProviderNumber;
  from: ProviderOrg;
  to: ProviderOrg;
  push: ProviderPush;
}

export interface ProviderTenure {
  organization_id?: string;
  organization_name: string;
  acquired_at?: unknown;
  released_at?: unknown;
  acquired_by?: string | null;
  released_by?: string | null;
  notes?: string | null;
}

export interface ProviderNumberHistory {
  number_id: number;
  e164: string;
  tenures: ProviderTenure[];
  pushes: SwitchPushRecord[];
}

const params = (providerOrgId?: string) => (providerOrgId ? { params: { providerOrgId } } : undefined);

export const providerNumbersService = {
  providers: async (): Promise<APIResponse<ProviderOrg[]>> => {
    try { return responseFrom<ProviderOrg[]>((await api.get(API_ENDPOINTS.PROVIDER_NUMBERS_PROVIDERS)).data); }
    catch (e: any) { return errorFrom<ProviderOrg[]>(e, 'Could not load your providers'); }
  },

  mine: async (providerOrgId?: string): Promise<APIResponse<ProviderNumbersList>> => {
    try { return responseFrom<ProviderNumbersList>((await api.get(API_ENDPOINTS.PROVIDER_NUMBERS_MINE, params(providerOrgId))).data); }
    catch (e: any) { return errorFrom<ProviderNumbersList>(e, 'Could not load your numbers'); }
  },

  targets: async (providerOrgId?: string): Promise<APIResponse<ProviderTargets>> => {
    try { return responseFrom<ProviderTargets>((await api.get(API_ENDPOINTS.PROVIDER_NUMBERS_TARGETS, params(providerOrgId))).data); }
    catch (e: any) { return errorFrom<ProviderTargets>(e, 'Could not load your organizations'); }
  },

  history: async (numberId: number, providerOrgId?: string): Promise<APIResponse<ProviderNumberHistory>> => {
    try {
      return responseFrom<ProviderNumberHistory>(
        (await api.get(API_ENDPOINTS.PROVIDER_NUMBER_HISTORY(numberId), params(providerOrgId))).data);
    } catch (e: any) { return errorFrom<ProviderNumberHistory>(e, 'Could not load the history'); }
  },

  reassign: async (numberId: number, targetOrganizationId: string, note?: string,
                   providerOrgId?: string): Promise<APIResponse<ProviderMoveResult>> => {
    try {
      return responseFrom<ProviderMoveResult>((await api.post(API_ENDPOINTS.PROVIDER_NUMBER_REASSIGN(numberId),
        { targetOrganizationId, note: note || undefined }, params(providerOrgId))).data);
    } catch (e: any) { return errorFrom<ProviderMoveResult>(e, 'Could not reassign the number'); }
  },

  returnToProvider: async (numberId: number, note?: string,
                           providerOrgId?: string): Promise<APIResponse<ProviderMoveResult>> => {
    try {
      return responseFrom<ProviderMoveResult>((await api.post(API_ENDPOINTS.PROVIDER_NUMBER_RETURN(numberId),
        { note: note || undefined }, params(providerOrgId))).data);
    } catch (e: any) { return errorFrom<ProviderMoveResult>(e, 'Could not return the number'); }
  },
};

export default providerNumbersService;
